import { useMemo, useCallback } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers';
import AddIcon from '@mui/icons-material/Add';

import { Button } from '@occc/ui-design-system';

import { parseTimeString, formatTimeToString } from '../../../utils/date-formatters';

type ScheduleTimeRangeProps = {
  startTime: string;
  endTime: string;
  onStartTimeChange: (value: string) => void;
  onEndTimeChange: (value: string) => void;
  showAddButton?: boolean;
  onAdd?: () => void;
  label?: string;
};

export const ScheduleTimeRange = ({
  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,
  showAddButton = false,
  onAdd,
  label = 'Time',
}: ScheduleTimeRangeProps) => {
  const startTimeDate = useMemo(() => parseTimeString(startTime), [startTime]);
  const endTimeDate = useMemo(() => parseTimeString(endTime), [endTime]);

  const handleStartTimeChange = useCallback(
    (value: Date | null) => onStartTimeChange(formatTimeToString(value)),
    [onStartTimeChange],
  );

  const handleEndTimeChange = useCallback(
    (value: Date | null) => onEndTimeChange(formatTimeToString(value)),
    [onEndTimeChange],
  );

  return (
    <Stack direction='row' spacing={3} alignItems='flex-end' justifyContent='space-between'>
      <Stack direction='row' spacing={4} alignItems='flex-end'>
        <Box>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
            {label}
          </Typography>
          <TimePicker
            label='From'
            value={startTimeDate}
            onChange={handleStartTimeChange}
            slotProps={{
              textField: {
                size: 'medium',
              },
            }}
          />
        </Box>
        <Box>
          <TimePicker
            label='To'
            value={endTimeDate}
            onChange={handleEndTimeChange}
            slotProps={{
              textField: {
                size: 'medium',
              },
            }}
          />
        </Box>
      </Stack>

      {showAddButton && onAdd && (
        <Button
          variant='contained'
          color='primary'
          startIcon={<AddIcon />}
          onClick={onAdd}
          sx={{ whiteSpace: 'nowrap' }}
        >
          ADD ANOTHER SCHEDULE BLOCK
        </Button>
      )}
    </Stack>
  );
};
